// The project viewer: a full-window page of sheets for one design project. Arrow keys, the arrows or a tap on a thumbnail
// move between sheets; a tap on a sheet toggles between fitting the window and full size (to read the small print).
import type { Sheet } from './projects';

export interface Step { label: string; fn: () => void }
export interface WorkOpts {
  title: string;
  text: string;
  sheets: Sheet[];
  onClose: () => void;
  prev?: Step; // the project before and after, on the map's order
  next?: Step;
}

let root: HTMLElement | null = null;
let stop: (() => void) | null = null;
export const isWorkOpen = (): boolean => root !== null;
export function closeWork(): void { stop?.(); }

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function openWork(o: WorkOpts): void {
  closeWork();
  const opener = document.activeElement as HTMLElement | null;
  const box = el('div', 'work');
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  box.setAttribute('aria-label', o.title);
  const back = el('button', 'work-back', '‹ Back to the map'); back.type = 'button';
  const head = el('header', 'work-head');
  head.append(el('h2', undefined, o.title), el('p', undefined, o.text));
  const stage = el('div', 'work-stage');
  const img = el('img'); img.decoding = 'async';
  const prevS = el('button', 'work-arrow left', '‹'); prevS.type = 'button'; prevS.setAttribute('aria-label', 'Previous sheet');
  const nextS = el('button', 'work-arrow right', '›'); nextS.type = 'button'; nextS.setAttribute('aria-label', 'Next sheet');
  const count = el('p', 'work-count');
  const note = el('p', 'work-note'); note.hidden = true; note.setAttribute('role', 'status');
  stage.append(img, prevS, nextS, count, note);
  const small = matchMedia('(pointer: coarse)').matches || innerWidth < 700; // phones load one sheet at a time and say so when it is slow
  const thumbs = el('div', 'work-thumbs');
  const tbs = o.sheets.map((s, i) => {
    const b = el('button'); b.type = 'button'; b.setAttribute('aria-label', `Sheet ${i + 1}`);
    if (!small) { const t = el('img'); t.src = s.src; t.alt = ''; t.loading = 'lazy'; b.appendChild(t); } else b.textContent = String(i + 1);
    b.classList.toggle('num', small); b.addEventListener('click', () => show(i)); thumbs.appendChild(b);
    return b;
  });
  const foot = el('footer', 'work-foot');
  const pb = el('button', 'step prev'); pb.type = 'button';
  const nb = el('button', 'step next'); nb.type = 'button';
  if (o.prev) { pb.textContent = `‹ ${o.prev.label}`; pb.addEventListener('click', () => { close(); o.prev?.fn(); }); } else pb.hidden = true;
  if (o.next) { nb.textContent = `${o.next.label} ›`; nb.addEventListener('click', () => { close(); o.next?.fn(); }); } else nb.hidden = true;
  foot.append(pb, nb);
  box.append(back, head, stage, thumbs, foot);
  document.body.appendChild(box);
  document.body.classList.add('paged');
  root = box;

  let cur = -1, slow = 0;
  const ahead = (i: number): void => { if (i >= 0 && i < o.sheets.length) new Image().src = o.sheets[i].src; };
  function show(i: number): void {
    cur = Math.max(0, Math.min(o.sheets.length - 1, i));
    const s = o.sheets[cur];
    stage.classList.remove('full'); stage.scrollTo(0, 0);
    clearTimeout(slow); note.hidden = true;
    img.onload = () => { clearTimeout(slow); note.hidden = true; ahead(cur + 1); };
    img.onerror = () => { clearTimeout(slow); note.textContent = 'This sheet did not load. Check the connection, or try the desktop page.'; note.hidden = false; };
    slow = window.setTimeout(() => { note.textContent = small ? 'Take a sip, have a seat, and view through the desktop page.' : 'Still loading this sheet…'; note.hidden = false; }, 3500);
    img.src = s.src; img.alt = s.alt;
    count.textContent = `${cur + 1} / ${o.sheets.length}  ·  tap to zoom`;
    tbs.forEach((b, k) => (k === cur ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
    prevS.hidden = nextS.hidden = o.sheets.length < 2;
    prevS.disabled = cur === 0; nextS.disabled = cur === o.sheets.length - 1;
    thumbs.hidden = o.sheets.length < 2;
  }
  prevS.addEventListener('click', () => show(cur - 1));
  nextS.addEventListener('click', () => show(cur + 1));
  img.addEventListener('click', () => { stage.classList.toggle('full'); });
  const keys = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); show(cur + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); show(cur - 1); }
  };
  addEventListener('keydown', keys);
  back.addEventListener('click', () => close());
  show(0);
  back.focus({ preventScroll: true });

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', keys);
    clearTimeout(slow);
    box.remove();
    document.body.classList.remove('paged');
    root = null; stop = null;
    opener?.focus?.({ preventScroll: true });
    o.onClose();
  }
  stop = close;
}
